import type { SubscriptionPlan } from '@/lib/api';

/**
 * Plan features, phrased the way the pricing design reads them.
 *
 * The plan table stores entitlements — `{ contacts: { enabled: true, limit: 2000 } }`.
 * Rendering that literally gives "Contacts: 2K" next to "Max Records Per Table:
 * Unlimited", which reads like a database dump. The design wants "2,000 contacts"
 * and "Shared inbox".
 *
 * 🔴 NOTHING HERE IS HARDCODED PER PLAN. Every bullet is derived from the plan's
 * own JSON, so a new plan, a renamed one, or a changed limit needs no code edit.
 * What is curated is only the PHRASING and the ORDER — presentation, not data.
 */

/** A number the user should see, or null when the feature is off. */
function usableLimit(entry: unknown): number | null {
  if (typeof entry === 'number') return entry === 0 ? null : entry;
  if (!entry || typeof entry !== 'object') return null;
  const e = entry as { enabled?: boolean; limit?: number | null };
  if (e.enabled === false) return null;
  const limit = e.limit;
  if (limit === null || limit === undefined) return e.enabled ? -1 : null;
  // Mirrors the entitlement rule: enabled with a 0 limit is NOT usable.
  if (limit === 0) return null;
  return limit;
}

function count(n: number): string {
  // Plain thousands grouping — the design shows "2,000" and "15,000".
  return n.toLocaleString('en-US');
}

/** "1 WhatsApp number" / "3 WhatsApp numbers" / "Unlimited WhatsApp numbers" */
function quantity(limit: number, singular: string, plural: string): string {
  if (limit === -1) return `Unlimited ${plural}`;
  return `${count(limit)} ${limit === 1 ? singular : plural}`;
}

/** Synthetic key: seats are derived from three sub-features, not one entry. */
const SEATS_KEY = '__seats__';

type Renderer = (limit: number) => string;

/**
 * Curated order and wording. Earlier entries win the limited space on a card,
 * so the things a buyer compares on come first.
 */
const BULLETS: Array<[key: string, render: Renderer]> = [
  ['business_accounts', (l) => quantity(l, 'WhatsApp number', 'WhatsApp numbers')],
  ['contacts', (l) => quantity(l, 'contact', 'contacts')],
  ['messages_per_month', (l) => (l === -1 ? 'Unlimited messages' : `${count(l)} messages / month`)],
  ['inbox', () => 'Shared inbox'],
  // Seats are a headline comparison point, so they sit in the ordered list
  // rather than being appended last where the cap would drop them.
  [SEATS_KEY, (l) => (l === -1 ? 'Unlimited team seats' : quantity(l, 'team seat', 'team seats'))],
  ['templates', (l) => quantity(l, 'template', 'templates')],
  ['campaigns', (l) => quantity(l, 'campaign', 'campaigns')],
  ['automations', (l) => quantity(l, 'automation', 'automations')],
  ['whatsapp_flows', (l) => quantity(l, 'WhatsApp form', 'WhatsApp forms')],
  ['contact_tags', (l) => quantity(l, 'contact tag', 'contact tags')],
  ['commerce', () => 'Catalog & commerce'],
  ['analytics', () => 'Analytics & reporting'],
  ['integrations', (l) => quantity(l, 'integration', 'integrations')],
  ['appointments', () => 'Appointments & scheduling'],
  ['data_store', () => 'Data store'],
  ['ai_generation', () => 'AI generation'],
  ['whatsapp_ai_agent', () => 'WhatsApp AI agent'],
  ['ai_calling', () => 'AI calling'],
  ['ads', () => 'Ads manager'],
  ['contact_export', () => 'Contact export'],
  ['petpooja', () => 'Petpooja integration'],
];

/**
 * Seats come from the three per-role caps summed, matching how the backend
 * derives `limits.users` — not from `max_users` alone, which undercounts.
 */
function seatCount(features: Record<string, unknown>): number | null {
  const team = features.team_management as
    | { sub_features?: Record<string, unknown> }
    | undefined;
  const subs = team?.sub_features;
  if (subs && typeof subs === 'object') {
    let total = 0;
    let sawOne = false;
    for (const key of ['max_admins', 'max_users', 'max_agents']) {
      const limit = usableLimit((subs as Record<string, unknown>)[key]);
      if (limit === -1) return -1;
      if (limit !== null) {
        total += limit;
        sawOne = true;
      }
    }
    if (sawOne) return total;
  }
  return usableLimit(team);
}

function parseFeatures(raw: unknown): Record<string, unknown> {
  if (!raw) return {};
  if (typeof raw === 'string') {
    try {
      return JSON.parse(raw) as Record<string, unknown>;
    } catch {
      return {};
    }
  }
  if (typeof raw === 'object' && !Array.isArray(raw)) return raw as Record<string, unknown>;
  return {};
}

export const MAX_PLAN_BULLETS = 9;

export function buildPlanFeatureBullets(plan: SubscriptionPlan): {
  seatsLine: string | null;
  bullets: string[];
} {
  const features = parseFeatures(plan.features);

  /**
   * A plan may ship hand-written marketing copy as `features.features`. When it
   * does, that is the intended wording and it wins outright.
   */
  const authored = (features as { features?: unknown }).features;
  if (Array.isArray(authored)) {
    const copy = authored.filter((x): x is string => typeof x === 'string');
    if (copy.length > 0) {
      const seats = seatCount(features);
      return {
        seatsLine: seats === null ? null : seats === -1 ? 'Unlimited seats' : `${seats} seat${seats === 1 ? '' : 's'}`,
        bullets: copy.slice(0, MAX_PLAN_BULLETS),
      };
    }
  }

  const seats = seatCount(features);

  const bullets: string[] = [];
  for (const [key, render] of BULLETS) {
    if (bullets.length >= MAX_PLAN_BULLETS) break;
    const limit = key === SEATS_KEY ? seats : usableLimit(features[key]);
    if (limit === null || limit === 0) continue;
    bullets.push(render(limit));
  }

  const seatsLine =
    seats === null ? null : seats === -1 ? 'Unlimited seats' : `${seats} seat${seats === 1 ? '' : 's'}`;

  return { seatsLine, bullets };
}
